import { DataContext } from "DataContainer";
import moment from "moment";
import React, { useContext, useMemo } from "react";
import { Container, Button, Row, Col, Card, Modal } from "reactstrap";
import { groupBy } from "views/content";
import SpeakerModal from "./SpeakerModal";

const ACCENT = "#C66067";
const ACCENT_MID = "#70AEB8";
const BTN_GOLD = "#C66067";
const CARD_BG = "rgba(255, 255, 255, 0.06)";
const CARD_BORDER = "rgba(255, 255, 255, 0.08)";
const MUTED = "rgba(255, 255, 255, 0.72)";

function Agenda() {
  const { agenda, speakers: allSpeakers } = useContext(DataContext);
  const dayWiseAgenda = groupBy("day")(agenda);
  const speakers = useMemo(
    () => allSpeakers.reduce((obj, item) => Object.assign(obj, { [item.id]: item }), {}),
    [allSpeakers],
  );

  const [open, toggleOpen] = React.useState({
    open: false,
    speaker: {},
  });
  const updateModal = (e, speaker) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    toggleOpen({
      open: !open.open,
      speaker: speaker !== undefined ? speaker : open.speaker,
    });
  };

  const size =
    document.body.clientWidth >= 1024 || document.documentElement.clientWidth >= 1024;

  function SpeakerCard({ speaker, isModerator }) {
    if (!speaker) return null;
    const colClass = size ? "col-md-6 col-lg-6" : "col-12";

    const focusCardHoverEnter = (e) => {
      const el = e.currentTarget;
      el.style.background = "rgba(198, 96, 103, 0.1)";
      el.style.borderColor = "rgba(198, 96, 103, 0.5)";
      el.style.transform = "translateY(-4px) scale(1.01)";
      el.style.boxShadow = "0 16px 40px rgba(198, 96, 103, 0.25)";
      const icon = el.querySelector(".focus-area-icon");
      if (icon) {
        icon.style.transform = "scale(1.06)";
        icon.style.filter = "drop-shadow(0 6px 12px rgba(198, 96, 103, 0.45))";
      }
    };
    const focusCardHoverLeave = (e) => {
      const el = e.currentTarget;
      el.style.background = "rgba(255, 255, 255, 0.05)";
      el.style.borderColor = "rgba(198, 96, 103, 0.2)";
      el.style.transform = "translateY(0) scale(1)";
      el.style.boxShadow = "none";
      const icon = el.querySelector(".focus-area-icon");
      if (icon) {
        icon.style.transform = "scale(1)";
        icon.style.filter = "drop-shadow(0 2px 6px rgba(198, 96, 103, 0.25))";
      }
    };

    return (
      <div className={`${colClass} mb-3`}>
        <Card
          className="focus-area-card mb-0"
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(198, 96, 103, 0.2)",
            borderRadius: "16px",
            padding: size ? "0.75rem 1rem" : "0.6rem 0.75rem",
            height: "100%",
            transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
          onMouseEnter={focusCardHoverEnter}
          onMouseLeave={focusCardHoverLeave}
          onClick={(e) => {
            if (e.target.closest("a[href='#speaker']")) return;
            updateModal(e, speaker);
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background:
                "linear-gradient(135deg, rgba(198, 96, 103, 0.05) 0%, rgba(112, 174, 184, 0.05) 100%)",
              opacity: 0,
              transition: "opacity 0.4s ease",
              pointerEvents: "none",
              borderRadius: "16px",
            }}
            className="gradient-overlay"
          />

          <div
            style={{
              flexShrink: 0,
              width: size ? "72px" : "60px",
              height: size ? "72px" : "60px",
              background: "linear-gradient(135deg, rgba(198, 96, 103, 0.2) 0%, rgba(112, 174, 184, 0.2) 100%)",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              border: "2px solid rgba(198, 96, 103, 0.3)",
              marginRight: size ? "0.85rem" : "0.65rem",
              transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <a
              href="#speaker"
              onClick={(e) => {
                e.stopPropagation();
                updateModal(e, speaker);
              }}
            >
              <img
                src={speaker.image}
                alt=""
                className="rounded-circle focus-area-icon"
                style={{
                  width: size ? 56 : 46,
                  height: size ? 56 : 46,
                  objectFit: "cover",
                  border: "2px solid rgba(198, 96, 103, 0.35)",
                  transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), filter 0.35s ease",
                  filter: "drop-shadow(0 2px 6px rgba(198, 96, 103, 0.25))",
                }}
              />
            </a>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              position: "relative",
              zIndex: 1,
              minWidth: 0,
            }}
          >
            {isModerator ? (
              <span
                className="d-inline-block"
                style={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  color: ACCENT,
                  textTransform: "uppercase",
                  marginBottom: "0.2rem",
                }}
              >
                Moderator
              </span>
            ) : null}
            <p
              className="text-400 text-white mb-1"
              style={{
                fontSize: size ? "0.9rem" : "0.82rem",
                lineHeight: 1.3,
                fontWeight: 700,
                letterSpacing: "0.3px",
              }}
            >
              {speaker.sTitle}
            </p>
            <p
              className="text-400 text-white mb-1"
              style={{
                fontSize: size ? "0.95rem" : "0.88rem",
                lineHeight: 1.35,
                fontWeight: 600,
                letterSpacing: "0.3px",
              }}
            >
              {speaker.name}
            </p>
            <p
              className="text-400 text-white mb-1"
              style={{
                fontSize: size ? "0.82rem" : "0.76rem",
                lineHeight: 1.4,
                fontWeight: 400,
                letterSpacing: "0.2px",
                opacity: 0.9,
              }}
            >
              {speaker.title}
            </p>
            <p
              className="text-400 text-white mb-0 text-i"
              style={{
                fontSize: size ? "0.78rem" : "0.72rem",
                lineHeight: 1.35,
                fontWeight: 600,
                letterSpacing: "0.2px",
                opacity: 0.85,
              }}
            >
              {speaker.company}
            </p>
          </div>

          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "48px",
              height: "48px",
              background: "linear-gradient(135deg, rgba(198, 96, 103, 0.1) 0%, transparent 100%)",
              borderRadius: "0 16px 0 100%",
              pointerEvents: "none",
            }}
          />
        </Card>
      </div>
    );
  }

  function AgendaSessionRow({ d, index }) {
    const timeLabel = `${moment.utc(d.startAt).format("HH:mm")} – ${moment.utc(d.endAt).format("HH:mm")}`;

    return (
      <Card
        key={d.id || `session-${index}`}
        className="mb-4 border-0"
        style={{
          background: CARD_BG,
          border: `1px solid ${CARD_BORDER} !important`,
          borderLeft: `4px solid ${ACCENT_MID}`,
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.35)",
        }}
      >
        <Container fluid className="py-4 px-3 px-md-4">
          <Row className="align-items-start">
            <Col lg={2} md={3} xs={12} className="mb-3 mb-lg-0">
              <div
                className="text-uppercase"
                style={{
                  fontWeight: 800,
                  fontSize: size ? "1.35rem" : "1.15rem",
                  letterSpacing: "0.02em",
                  color: ACCENT,
                  lineHeight: 1.2,
                }}
              >
                {timeLabel}
              </div>
              {d.type ? (
                <span
                  className="d-inline-block mt-2"
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: "#1a1a1a",
                    background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_MID})`,
                    padding: "0.35rem 0.65rem",
                    borderRadius: "4px",
                  }}
                >
                  {d.type}
                </span>
              ) : null}
            </Col>
            <Col lg={10} md={9} xs={12}>
              <h3
                className="text-white mb-3 mt-0"
                style={{
                  fontSize: size ? "1.35rem" : "1.1rem",
                  fontWeight: 700,
                  lineHeight: 1.35,
                }}
              >
                {d.title}
              </h3>
              {d.description ? (
                <div
                  className="mb-4 agenda-description"
                  style={{
                    fontSize: size ? "1rem" : "0.95rem",
                    lineHeight: 1.65,
                  }}
                  dangerouslySetInnerHTML={{ __html: d.description }}
                />
              ) : null}
              <Row className="mx-n2">
                {d.type === "Panel Discussion" ? (
                  <>
                    {d.moderator ? <SpeakerCard speaker={speakers[d.moderator]} isModerator /> : null}
                    {d.speakers &&
                      d.speakers.map((sid) => <SpeakerCard key={sid} speaker={speakers[sid]} />)}
                  </>
                ) : (
                  d.speakers &&
                  d.speakers.map((sid) => <SpeakerCard key={sid} speaker={speakers[sid]} />)
                )}
              </Row>
            </Col>
          </Row>
        </Container>
      </Card>
    );
  }

  const dayBlocks = [1, 2]
    .map((day) => ({ day, items: dayWiseAgenda[day] || [] }))
    .filter((b) => b.items.length > 0);

  return (
    <>
      <div
        style={{
          background: "#000",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          paddingTop: size ? "6rem" : "4rem",
        }}
        className="section section-with-space pb-5"
        data-parallax={true}
      >
        <Container>
          <Row className="justify-content-center mb-4 mb-md-5">
            <Col md={10} lg={8} className="text-center">
              <h1
                className="text-center mb-3"
                style={{
                  fontSize: size ? "3rem" : "2rem",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_MID} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Conference Agenda
              </h1>
              <div
                style={{
                  width: "100px",
                  height: "4px",
                  background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_MID})`,
                  borderRadius: "2px",
                  margin: "0 auto 1.25rem",
                }}
              />
              <p className="mb-0" style={{ color: MUTED, fontSize: size ? "1.05rem" : "0.95rem", lineHeight: 1.6 }}>
                Session times, formats, and speakers at a glance.
              </p>
            </Col>
          </Row>

          {dayBlocks.map(({ day, items }) => (
            <React.Fragment key={day}>
              {dayBlocks.length > 1 ? (
                <Row className="mb-3">
                  <Col>
                    <h2
                      className="text-white mb-0"
                      style={{
                        fontSize: size ? "1.5rem" : "1.2rem",
                        fontWeight: 600,
                        borderBottom: `1px solid ${CARD_BORDER}`,
                        paddingBottom: "0.75rem",
                      }}
                    >
                      Day {day}
                    </h2>
                  </Col>
                </Row>
              ) : null}
              {items.map((d, index) => (
                <AgendaSessionRow key={d.id || `${day}-${index}`} d={d} index={index} />
              ))}
            </React.Fragment>
          ))}

          <Row>
            <Col lg={12} className="pt-4 text-center">
              <Button
                href="/register"
                className="rounded-0 navbar-item-custom text-400 text-white px-5"
                color="info"
                size="lg"
                style={{ background: BTN_GOLD, borderColor: BTN_GOLD }}
              >
                <span
                  style={{
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "18px",
                  }}
                >
                  Register now
                </span>
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={open.open} toggle={(e) => updateModal(e)} style={{}}>
        <div className="section profile-content py-0">
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={(e) => updateModal(e)}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#888888",
              zIndex: 2,
            }}
          >
            <span aria-hidden={true}>×</span>
          </button>
          <SpeakerModal speaker={open.speaker} />
        </div>
      </Modal>

      <style>{`
        .agenda-description p,
        .agenda-description span {
          color: #fff !important;
        }
        .agenda-description a,
        .agenda-description a span {
          color: ${ACCENT} !important;
          text-decoration: underline;
        }
        .agenda-description a:hover,
        .agenda-description a:hover span {
          color: ${ACCENT_MID} !important;
        }
        .agenda-description p:last-child { margin-bottom: 0; }
        .agenda-description b,
        .agenda-description strong {
          font-weight: 800 !important;
        }
        .agenda-description a b,
        .agenda-description a strong {
          color: inherit !important;
          font-weight: 800 !important;
        }
      `}</style>
    </>
  );
}

export default Agenda;
